<script setup lang="ts">
import { useMate } from "@/stores/mate";
import { MateSprite } from "@/types/game";
import { ref, computed } from "vue";
import { useHouseSession } from "../../store/houseSession";
import { fixNumber } from "@/components/Mission/MissionStat";
import { IDEA_INTERVAL_SEC, useHouseScene } from "../../store/houseScene";
import { showLoadingToast } from "vant";
import { useInteractive } from "@/stores/interactive";
import SpriteSelectorPopup from "@/components/character-generator/SpriteSelectorPopup.vue";
import RelationshipPopup from "./RelationshipPopup.vue";
import { useGlobalUser } from "@/stores/globalUser";
import CharacterInvitePopup from "../CharacterInvitePopup.vue";

const HOUSE_MAX_COUNT = 8;

const props = defineProps<{
  loading: boolean;
  extraPower: number;
}>();
const emit = defineEmits<{
  (e: "sprite-click", spriteId: number): void;
  (e: "showHouseSetting"): void;
  (e: "toggleAllAcitveThought"): void;
}>();

const houseStore = useHouseSession();
const mateStore = useMate();
const houseSceneStore = useHouseScene();
const interactiveStore = useInteractive();
const userStore = useGlobalUser();

// const showSpriteSelector = ref(false);
const selectedSprites = computed<MateSprite[]>(
  () => houseStore.houseSession?.mateSprites ?? []
);
const validMates = computed(() =>
  selectedSprites.value.filter(
    (sprite) => mateStore.getMyMateById(sprite.mateId!)?.isHouseEnabled
  )
);
const selectedSpriteIds = computed<number[]>(() =>
  selectedSprites.value.map((m) => m.id!)
);

const onSpriteSelectorConfirm = async (spriteIds: number[]) => {
  const loading = showLoadingToast("");
  try {
    await houseStore.updateHouseSession(spriteIds);
  } finally {
    loading.close();
  }
};

const estimateThoughtCost = computed(() => {
  return fixNumber(
    validMates.value.length *
      Math.floor(60 / IDEA_INTERVAL_SEC) *
      (1 + props.extraPower)
  );
});
const estimateTotalCost = computed(() => {
  return estimateThoughtCost.value + interactiveStore.estimatedPowerCubes;
});
const showRelationshipPopup = ref(false);

const showInvitePopup = ref(false);
</script>
<template>
  <div
    v-if="houseStore.activeTab === 'members'"
    class="h-full overflow-y-auto p-2 flex flex-col gap-2"
  >
    <div class="flex items-center justify-between">
      <div class="join">
        <button
          v-if="false"
          @click="showRelationshipPopup = true"
          class="join-item btn btn-xs rounded btn-outline"
        >
          {{ $t("house.relationship") }}
        </button>

        <!-- <button
          @click="showSpriteSelector = true"
          class="join-item btn btn-xs rounded btn-outline"
        >
          <i class="iconify mingcute--user-add-2-line" />
          {{ $t("house.memberManage") }}
        </button> -->

        <button
          @click="mateStore.clickCreateMateOption()"
          class="join-item btn btn-primary btn-xs rounded btn-outline"
        >
          <i class="iconify ph--address-book-tabs-light" />
          {{ $t("house.invite") }}
        </button>

        <button
          v-if="false"
          class="join-item btn btn-xs rounded btn-outline"
          @click="emit('showHouseSetting')"
        >
          <i class="iconify mingcute--settings-3-line w-4"></i>
        </button>
      </div>
      <div class="text-xs text-base-content/80 flex items-center gap-1">
        <div class="join flex items-center gap-1" v-if="validMates.length">
          <span
            v-if="!userStore.isFreeModel()"
            class="flex items-center gap-1 text-xs text-base-content/50 join-tem"
          >
            {{ $t("house.perMin") }}

            <PowerIcon class="icon-xs relative -top-[1px]" />
            {{
              userStore.getPowerCostByModel(
                validMates.length *
                  Math.floor(60 / IDEA_INTERVAL_SEC) *
                  (1 + extraPower)
              )
            }}
          </span>
          <SelectModelBtn tips="left" />
        </div>
        <button
          class="join-item btn btn-xs rounded relative btn-outline"
          @click="emit('toggleAllAcitveThought')"
          :class="{
            'line-through decoration-2 decoration-error':
              !houseStore.isThoughtActivated,
          }"
        >
          💬
          {{ $t("house.thought") }}
        </button>
      </div>
    </div>
    <div class="grid grid-cols-5 gap-2">
      <div
        v-for="item in houseStore.houseSession?.mateSprites"
        :key="item.id"
        class="grid grid-rows-[1fr_auto] items-center justify-center w-full h-full"
      >
        <div
          class="rounded cursor-pointer h-full w-full aspect-[3/4] relative"
          :class="{
                'border border-primary bg-base-300':
                  houseSceneStore.followingCharId === item.id!.toString(),
              }"
          @click="emit('sprite-click', item.id!)"
        >
          <VanImage
            :src="item.imageMedia?.url"
            class="w-full h-full"
            fit="contain"
          />
          <div
            v-if="
                  mateStore.getMyMateById(item.mateId!)?.isHouseEnabled ??
                  item.mate?.isHouseEnabled
                "
            class="bg-success w-1.5 h-1.5 rounded-full border-success absolute top-1 right-1"
          />
        </div>
        <div class="w-full text-center line-clamp-1 text-xs my-1">
          {{ item.mate?.name }}
        </div>
        <button
          class="btn btn-outline btn-xs rounded"
          @click="mateStore.toMateHome(item.mate, true)"
        >
          {{ $t("Buttons.edit") }}
        </button>
      </div>
      <div
        class="rounded cursor-pointer h-full w-full relative grid-rows-[1fr_auto] items-center justify-center"
        v-if="validMates.length <= HOUSE_MAX_COUNT"
      >
        <div
          @click="houseStore.showSpriteSelector = true"
          class="w-full border border-dashed rounded flex items-center justify-center text-base-content/50 cursor-pointer aspect-[3/4]"
        >
          <i class="iconify mingcute--add-circle-line" />
        </div>
      </div>
    </div>
    <SpriteSelectorPopup
      v-if="!loading"
      v-model:show="houseStore.showSpriteSelector"
      :selected-sprite-ids="selectedSpriteIds"
      @on-confirm="onSpriteSelectorConfirm"
      :max-count="HOUSE_MAX_COUNT"
      show-mate-without-sprite
      enable-empty
    />
    <RelationshipPopup v-model:show="showRelationshipPopup" />

    <CharacterInvitePopup v-model:show="showInvitePopup" />
  </div>
</template>
